import { Chips } from '@cian/ui-kit';

import { type FC, useCallback, useState } from 'react';

import { type IChipsDataService } from 'shared/services/ChipsDataService';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { SliderBlock } from '../SliderBlock';
import { VerticalArrow } from '../VerticalArrow';

import { type ISliderBlockTypeOneModel } from './external/models/SliderBlockTypeOneModel';
import { Slide as SlideV1 } from './internal/components/Slide';
import { Slide as SlideV2 } from './internal/components/SlideV2';

import styles from './SliderBlockTypeOne.css';

type SliderBlockTypeOneProps = {
  id: string;
  title: string;
  description?: string;
  sliderBlockTypeOneSlideService: IChipsDataService<ISliderBlockTypeOneModel>;
  onShow: () => void;
  /** Аналитика смены чипа — вызывается из контейнера (трекинг не в UI-слое). */
  onChipSelect?: (sliderBlockTypeOneModel: ISliderBlockTypeOneModel) => void;
  isDesignV2Enabled?: boolean;
};

/**
 * @description Слайдер типа один. Слайдер представляет собой виджет, который содержит в себе список слайдов и чипы для выбора набора слайдов.
 * Сам слайд содержит в себе изображение снизу, заголовок и описание сверху.
 * @param id - Идентификатор слайдера.
 * @param title - Заголовок слайдера.
 * @param description - Описание слайдера.
 * @param sliderBlockTypeOneSlideService - Сервис данных слайдера.
 */
export const SliderBlockTypeOne: FC<SliderBlockTypeOneProps> = ({
  id,
  title,
  description,
  sliderBlockTypeOneSlideService,
  onShow,
  onChipSelect,
  isDesignV2Enabled = false,
}) => {
  const { logger } = useApplicationContext();
  const Slide = isDesignV2Enabled ? SlideV2 : SlideV1;

  const [activeDefaultSliderBlockSlide, setActiveDefaultSliderBlockSlide] = useState<ISliderBlockTypeOneModel>(
    sliderBlockTypeOneSlideService.defaultItem,
  );

  const handleChange = useCallback<(value: string[]) => void>(
    value => {
      const chipValue = value[0];
      const activeItem = sliderBlockTypeOneSlideService.getActiveItem(chipValue, logger);

      onChipSelect?.(activeItem);
      setActiveDefaultSliderBlockSlide(activeItem);
    },
    [logger, onChipSelect, sliderBlockTypeOneSlideService],
  );

  return (
    <SliderBlock
      arrow={VerticalArrow}
      contentOffset={null}
      description={description}
      id={id}
      rightAdornment={
        <div className={styles['chips']}>
          <Chips<string>
            dataTestId={`${id}-chips`}
            options={sliderBlockTypeOneSlideService.chipsOptions}
            value={[activeDefaultSliderBlockSlide.chipsOption.value]}
            onChange={handleChange}
          />
        </div>
      }
      sliderKey={activeDefaultSliderBlockSlide.chipsOption.value}
      title={title}
      onShow={onShow}
    >
      {activeDefaultSliderBlockSlide.slides.map((slide, index) => (
        <Slide description={slide.description} imageUrl={slide.imageUrl} key={index} title={slide.title} />
      ))}
    </SliderBlock>
  );
};
